/* eslint-disable react-hooks/exhaustive-deps */
import {FC, useContext, useMemo} from 'react'
import {useQuery} from 'react-query'
import {
  createResponseContext,
  initialQueryResponse,
  initialQueryState,
  PaginationState,
  QUERIES,
  stringifyRequestQuery,
  WithChildren,
} from '../../../../_metronic/helpers'
import {listPeople} from './_requests'
import {People} from './_models'
import {useQueryRequest} from './QueryRequestProvider'
import Swal from 'sweetalert2'

const QueryResponseContext = createResponseContext<People>(initialQueryResponse)
const QueryResponseProvider: FC<WithChildren> = ({children}) => {
  const {state} = useQueryRequest()
  const query = useMemo(() => stringifyRequestQuery(state), [state])

  const hasSearched = useMemo(() => {
    const {name, phone, document, status} = state
    return Boolean(name || phone || document || status)
  }, [state])

  const {
    isFetching,
    refetch,
    data: response,
  } = useQuery(
    `${QUERIES.PEOPLE_LIST}-${query}`,
    async () => {
      try {
        return await listPeople(query)
      } catch (error: any) {
        await Swal.fire({
          icon: 'warning',
          title: 'Oops...',
          text: error?.response?.data?.message,
          showConfirmButton: true,
          customClass: {
            confirmButton: 'btn btn-danger',
            cancelButton: 'btn btn-secondary',
          },
        })
      }
    },
    {cacheTime: 0, keepPreviousData: true, refetchOnWindowFocus: false, enabled: hasSearched}
  )

  return (
    <QueryResponseContext.Provider
      value={{isLoading: isFetching, refetch, response, query, hasSearched}}
    >
      {children}
    </QueryResponseContext.Provider>
  )
}

const useQueryResponse = () => useContext(QueryResponseContext)

const useQueryResponseData = () => {
  const {response} = useQueryResponse()
  if (!response) {
    return []
  }

  return response?.data || []
}

const useQueryResponsePagination = () => {
  const defaultPaginationState: PaginationState = {
    links: [],
    ...initialQueryState,
  }

  const {response} = useQueryResponse()

  if (!response || !response.meta) {
    return defaultPaginationState
  }

  const pagination: PaginationState = {
    page: response.meta.current_page,
    per_page: response.meta.per_page,
    links: response.meta.links?.map((link) => {
      const pageMatch = String(link.url).match(/page=(\d+)/)
      const pageValue = pageMatch ? parseInt(pageMatch[1]) : null
      return {...link, page: pageValue}
    }),
    total: response.meta.total,
  }

  return pagination
}

const useQueryResponseLoading = (): boolean => {
  const {isLoading} = useQueryResponse()
  return isLoading
}

const useQueryResponseHasSearched = (): boolean => {
  const {hasSearched} = useQueryResponse()
  return Boolean(hasSearched)
}

export {
  QueryResponseProvider,
  useQueryResponse,
  useQueryResponseData,
  useQueryResponsePagination,
  useQueryResponseLoading,
  useQueryResponseHasSearched,
}
